/**
 * BRE-9118: React's hydration errors (#418, #423, #425) reach PostHog with no
 * stack and no component: Next 16's `onRecoverableError` drops React's
 * `errorInfo` and the App Router offers no hook to get it back. So a stackless
 * hydration `$exception` is stamped with the names (never values) of DOM nodes
 * and attributes a browser extension typically adds, which is what tells an
 * extension apart from a real server/client divergence.
 *
 * On the auth pages, where only password managers and autofill were ever seen
 * causing it and sign-in still completes, the event is renamed so Error
 * Tracking never pages on it. Everywhere else it stays a `$exception`.
 */

import type { CaptureResult } from "posthog-js";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

const HYDRATION_ERROR = /Minified React error #(418|423|425)\b/;
const AUTH_PATHS = new Set(["/login", "/signup", "/invite"]);
const MAX_FINGERPRINT_ENTRIES = 20;
/** Attributes the root layout and theme script put on `<html>`/`<body>` themselves. */
const APP_ROOT_ATTRIBUTES = new Set(["lang", "translate", "class", "style", "dir", "data-theme"]);
/** Prefixes password managers, autofill and writing assistants add to form fields. */
const EXTENSION_FIELD_ATTRIBUTE =
  /^(data-(lastpass|lpignore|1p|op-|bwautofill|bitwarden|dashlane|gr-|grammarly|kwimpala|ms-editor|com-onepassword)|data-kwimpalastatus|data-ms-editor|cz-shortcut)/i;

/** Route code ships under this path; React's scheduler and react-dom frames never do, so only these count as a stack (BRE-9520). */
const APP_CHUNK_PATH = "/_next/static/chunks/app/";

interface ExceptionListItem {
  value?: unknown;
  stacktrace?: unknown;
}

function hasStackFrames(item: ExceptionListItem): boolean {
  const frames = (item.stacktrace as { frames?: unknown } | null | undefined)?.frames;
  return Array.isArray(frames) && frames.some(isAppFrame);
}

function isAppFrame(frame: unknown): boolean {
  const filename = (frame as { filename?: unknown } | null)?.filename;
  return typeof filename === "string" && filename.includes(APP_CHUNK_PATH);
}

function isStacklessHydrationItem(item: unknown): boolean {
  const { value } = (item ?? {}) as ExceptionListItem;
  return typeof value === "string" && HYDRATION_ERROR.test(value) && !hasStackFrames(item as ExceptionListItem);
}

/** Every exception in the event is a stackless React hydration error. */
export function isStacklessHydrationMismatch(cr: CaptureResult | null): boolean {
  if (!cr || cr.event !== "$exception") return false;
  const list = (cr.properties as Record<string, unknown> | undefined)?.$exception_list;
  return Array.isArray(list) && list.length > 0 && list.every(isStacklessHydrationItem);
}

/**
 * Tag and attribute names only: custom elements anywhere, unexpected
 * attributes on `<html>`/`<body>`, and extension attributes on form fields.
 */
export function hydrationDomFingerprint(): string[] {
  const names = new Set<string>();
  const add = (name: string) => {
    if (names.size < MAX_FINGERPRINT_ENTRIES) names.add(name);
  };
  for (const root of [document.documentElement, document.body]) {
    for (const attr of root?.getAttributeNames() ?? []) {
      if (!APP_ROOT_ATTRIBUTES.has(attr)) add(`${root.tagName.toLowerCase()}@${attr}`);
    }
  }
  for (const field of Array.from(document.querySelectorAll("form, input, textarea"))) {
    for (const attr of field.getAttributeNames()) {
      if (EXTENSION_FIELD_ATTRIBUTE.test(attr)) add(`${field.tagName.toLowerCase()}@${attr}`);
    }
  }
  for (const el of Array.from(document.body?.querySelectorAll("*") ?? [])) {
    const tag = el.tagName.toLowerCase();
    if (tag.includes("-")) add(tag);
  }
  return Array.from(names);
}

function isAuthPath(pathname: unknown): boolean {
  if (typeof pathname !== "string") return false;
  return AUTH_PATHS.has(pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname);
}

/** posthog-js `before_send` step. Returns a new event; fails open with the original. */
export function routeHydrationMismatch(cr: CaptureResult | null): CaptureResult | null {
  try {
    if (!cr || !isStacklessHydrationMismatch(cr)) return cr;
    const properties: Record<string, unknown> = { ...cr.properties, hydration_dom_fingerprint: hydrationDomFingerprint() };
    const event = isAuthPath(properties.$pathname) ? ANALYTICS_EVENTS.AUTH_HYDRATION_MISMATCH_RECOVERED : cr.event;
    return { ...cr, event, properties };
  } catch {
    return cr;
  }
}
